import { useState } from "react";
import { HFButton } from "@/components/ui-hf";
import { currentVtCode, trackConversion } from "@/lib/analytics";
import {
    OBJECT_TYPES,
    PACKAGE_OPTIONS,
} from "@/lib/homeContent";
import { Paperclip, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

type Props = {
    initialPackage?: string;
    testIdPrefix?: string;
    /** Kategorie-Vorwahl (nur /erleben): wird als hidden Form-Feld
     *  `category` an das Backend übermittelt. */
    initialCategory?: string;
    /** Sichtbare Serifen-Zeile über dem Formular (z. B. „Für Familien"). */
    visibleCategoryLabel?: string;
    /** `dark` = QR-Landing-Ästhetik (dunkler BG, gold Akzente). */
    variant?: "default" | "dark";
};

type Status = "idle" | "sending" | "success" | "error";

export default function ContactForm({
    initialPackage = "",
    testIdPrefix = "contact",
    initialCategory = "",
    visibleCategoryLabel = "",
    variant = "default",
}: Props) {
    const [status, setStatus] = useState<Status>("idle");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const [pkg, setPkg] = useState(initialPackage);
    const [datenschutz, setDatenschutz] = useState(false);
    const [files, setFiles] = useState<FileList | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{
        name?: string; email?: string; message?: string; datenschutz?: string;
    }>({});

    /** Aktuelle Kategorie (folgt der Prop, damit PillarCard-Klicks das
     *  Feld beim Prop-Wechsel neu setzen). */
    const isDark = variant === "dark";

    /** Normalisiert Backend-Fehler zu einem darstellbaren String.
     *  Deckt FastAPI-422 (Array-of-Objects), HTTPException (String) und
     *  fetch-Errors ab — verhindert das „Objects are not valid as a React child"-Crash. */
    function toErrorText(payload: unknown, fallbackHttp: string): string {
        if (!payload || typeof payload !== "object") return fallbackHttp;
        const detail = (payload as { detail?: unknown }).detail;
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) {
            const parts = detail
                .map((d: unknown) => {
                    if (!d || typeof d !== "object") return "";
                    const dd = d as { loc?: unknown[]; msg?: string };
                    const loc = Array.isArray(dd.loc)
                        ? dd.loc.filter((x) => x !== "body").join(".")
                        : "";
                    const msg = dd.msg || "";
                    return loc ? `${loc}: ${msg}` : msg;
                })
                .filter(Boolean);
            if (parts.length > 0) return parts.join(" · ");
        }
        const m = (payload as { message?: string }).message;
        if (typeof m === "string") return m;
        return fallbackHttp;
    }

    /** Client-seitige Basis-Validierung. Liefert `null` wenn alles ok,
     *  sonst ein Map<Feld,String> mit deutscher Erklärung.  */
    function validate(fd: FormData): typeof fieldErrors {
        const errors: typeof fieldErrors = {};
        const name = String(fd.get("name") || "").trim();
        const email = String(fd.get("email") || "").trim();
        const message = String(fd.get("message") || "").trim();
        if (!name) errors.name = "Bitte geben Sie Ihren Namen an.";
        if (!email) errors.email = "Bitte geben Sie eine E-Mail-Adresse an.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            errors.email = "Diese E-Mail-Adresse wirkt unvollständig.";
        if (!message) errors.message = "Bitte hinterlassen Sie uns eine kurze Nachricht.";
        if (!datenschutz) errors.datenschutz = "Bitte bestätigen Sie die Datenschutzerklärung.";
        return errors;
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMsg("");
        const form = e.currentTarget;
        const fd = new FormData();
        fd.append("name", (form.elements.namedItem("name") as HTMLInputElement).value);
        fd.append("firma", (form.elements.namedItem("firma") as HTMLInputElement).value);
        fd.append("email", (form.elements.namedItem("email") as HTMLInputElement).value);
        fd.append("phone", (form.elements.namedItem("phone") as HTMLInputElement).value);
        fd.append("object_type", (form.elements.namedItem("object_type") as HTMLSelectElement).value);
        fd.append("location", (form.elements.namedItem("location") as HTMLInputElement).value);
        fd.append("package", pkg);
        fd.append("grundstueck", (form.elements.namedItem("grundstueck") as HTMLInputElement).value);
        fd.append("wunschtermin", (form.elements.namedItem("wunschtermin") as HTMLInputElement).value);
        fd.append("message", (form.elements.namedItem("message") as HTMLTextAreaElement).value);
        fd.append("datenschutz", datenschutz ? "true" : "false");
        fd.append("category", initialCategory || "");
        // Vertreter-Tracking-Code aus sessionStorage (vom QR-Link gesetzt).
        const vt = currentVtCode();
        if (vt) fd.append("vt_code", vt);
        // Referenz-Aktion: wenn diese Session über die UTM-Kampagne kam,
        // markieren wir die Anfrage als `source=referenzaktion` — Admin
        // kann darauf filtern und Analytics wertet's aus.
        try {
            if (sessionStorage.getItem("hf_aktion_active") === "1") {
                fd.append("source", "referenzaktion");
            }
        } catch { /* ignore */ }
        fd.append("honeypot", (form.elements.namedItem("website") as HTMLInputElement).value);

        // Client-Validation — erst Roundtrip vermeiden, dann sauber ausliefern.
        const clientErrors = validate(fd);
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            setStatus("idle");
            return;
        }
        setFieldErrors({});
        setStatus("sending");

        if (files) {
            Array.from(files).forEach((f) => fd.append("files", f, f.name));
        }
        try {
            const res = await fetch(`${BACKEND_URL}/api/contact`, {
                method: "POST",
                body: fd,
            });
            let data: unknown = null;
            try { data = await res.json(); } catch { /* body not JSON */ }
            if (!res.ok) {
                setStatus("error");
                setErrorMsg(toErrorText(data, `Fehler (HTTP ${res.status}).`));
                return;
            }
            setStatus("success");
            form.reset();
            setFiles(null);
            setDatenschutz(false);
            // Analytics: erfolgreicher Conversion-Event (Anfrage abgeschickt).
            try {
                trackConversion({
                    object_type: fd.get("object_type"),
                    location: fd.get("location"),
                    category: fd.get("category"),
                    has_vt: !!fd.get("vt_code"),
                });
                // Separater Aktion-Event, damit die CTA-Impact-Karte
                // die tatsächliche Aktions-Konversion tracken kann.
                if (fd.get("source") === "referenzaktion") {
                    // Lazy-load, um Zyklen zu vermeiden.
                    import("@/lib/analytics").then((m) => m.trackAktion("form_submitted"));
                }
            } catch {
                /* nicht kritisch */
            }
        } catch (err) {
            setStatus("error");
            setErrorMsg((err as Error).message || "Verbindung fehlgeschlagen.");
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid={`${testIdPrefix}-success`}
                className="p-8 rounded"
                style={{
                    background: "hsl(var(--hf-surface))",
                    border: "1px solid hsl(var(--hf-line))",
                }}
            >
                <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                    style={{ background: "hsl(var(--hf-gold-soft) / 0.7)" }}
                >
                    <Check size={22} style={{ color: "hsl(var(--hf-gold-dark))" }} />
                </div>
                <h3 className="font-display text-2xl md:text-3xl mt-5">
                    Vielen Dank für Ihre Anfrage.
                </h3>
                <p className="mt-3 text-hf-muted">
                    Wir haben Ihre Nachricht sicher empfangen und melden uns innerhalb von 1–2 Werktagen persönlich bei Ihnen.
                </p>
                <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="hf-btn hf-btn-ghost mt-6"
                    data-testid={`${testIdPrefix}-again`}
                >
                    Neue Anfrage senden
                </button>
            </div>
        );
    }

    const input =
        "w-full px-4 py-3 rounded-sm bg-white border text-sm text-hf-ink placeholder:text-hf-muted focus:outline-none focus:ring-2 focus:ring-[hsl(var(--hf-gold))] focus:border-transparent transition";
    const label = "block text-sm text-hf-ink mb-1.5 font-medium";

    return (
        <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid={`${testIdPrefix}-form`}
            noValidate
        >
            {/* Honeypot (visually hidden) */}
            <label
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                }}
            >
                Website
                <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                />
            </label>

            <div className="md:col-span-1">
                <label htmlFor="ct-name" className={label}>
                    Name <span style={{ color: "hsl(var(--hf-gold-dark))" }}>*</span>
                </label>
                <input
                    id="ct-name"
                    name="name"
                    type="text"
                    required
                    aria-invalid={!!fieldErrors.name}
                    className={input}
                    style={{ borderColor: fieldErrors.name ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-name`}
                />
                {fieldErrors.name && (
                    <p className="mt-1 text-xs" style={{ color: "hsl(var(--hf-gold-dark))" }} data-testid={`${testIdPrefix}-err-name`}>
                        {fieldErrors.name}
                    </p>
                )}
            </div>
            <div className="md:col-span-1">
                <label htmlFor="ct-firma" className={label}>Firma (optional)</label>
                <input
                    id="ct-firma"
                    name="firma"
                    type="text"
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-firma`}
                />
            </div>
            <div>
                <label htmlFor="ct-email" className={label}>
                    E-Mail <span style={{ color: "hsl(var(--hf-gold-dark))" }}>*</span>
                </label>
                <input
                    id="ct-email"
                    name="email"
                    type="email"
                    required
                    aria-invalid={!!fieldErrors.email}
                    className={input}
                    style={{ borderColor: fieldErrors.email ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-email`}
                />
                {fieldErrors.email && (
                    <p className="mt-1 text-xs" style={{ color: "hsl(var(--hf-gold-dark))" }} data-testid={`${testIdPrefix}-err-email`}>
                        {fieldErrors.email}
                    </p>
                )}
            </div>
            <div>
                <label htmlFor="ct-phone" className={label}>Telefon</label>
                <input
                    id="ct-phone"
                    name="phone"
                    type="tel"
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-phone`}
                />
            </div>
            <div>
                <label htmlFor="ct-object" className={label}>Art des Objekts</label>
                <select
                    id="ct-object"
                    name="object_type"
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    defaultValue=""
                    data-testid={`${testIdPrefix}-field-object`}
                >
                    <option value="">Bitte wählen…</option>
                    {OBJECT_TYPES.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            </div>
            <div>
                <label htmlFor="ct-location" className={label}>Ort / PLZ</label>
                <input
                    id="ct-location"
                    name="location"
                    type="text"
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-location`}
                />
            </div>
            <div>
                <label htmlFor="ct-package" className={label}>Gewünschte Leistung</label>
                <select
                    id="ct-package"
                    value={pkg}
                    onChange={(e) => setPkg(e.target.value)}
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-package`}
                >
                    <option value="">Bitte wählen…</option>
                    {PACKAGE_OPTIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
            </div>
            <div>
                <label htmlFor="ct-groesse" className={label}>Grundstücksgröße (optional)</label>
                <input
                    id="ct-groesse"
                    name="grundstueck"
                    type="text"
                    placeholder="z.B. 850 m²"
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-grundstueck`}
                />
            </div>
            <div className="md:col-span-2">
                <label htmlFor="ct-wunschtermin" className={label}>Wunschtermin (optional)</label>
                <input
                    id="ct-wunschtermin"
                    name="wunschtermin"
                    type="text"
                    placeholder="z.B. April – Mai 2026, vormittags"
                    className={input}
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-wunschtermin`}
                />
            </div>
            <div className="md:col-span-2">
                <label htmlFor="ct-message" className={label}>
                    Ihre Nachricht <span style={{ color: "hsl(var(--hf-gold-dark))" }}>*</span>
                </label>
                <textarea
                    id="ct-message"
                    name="message"
                    required
                    rows={5}
                    placeholder="Kurz zum Objekt und was Ihnen wichtig ist."
                    aria-invalid={!!fieldErrors.message}
                    className={input}
                    style={{ borderColor: fieldErrors.message ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-field-message`}
                />
                {fieldErrors.message && (
                    <p className="mt-1 text-xs" style={{ color: "hsl(var(--hf-gold-dark))" }} data-testid={`${testIdPrefix}-err-message`}>
                        {fieldErrors.message}
                    </p>
                )}
            </div>
            <div className="md:col-span-2">
                <label htmlFor="ct-files" className={label}>Anhang (optional · jpg/png/pdf, max 25 MB gesamt)</label>
                <label
                    htmlFor="ct-files"
                    className="flex items-center gap-2 px-4 py-3 rounded-sm border-dashed border-2 border-hf-line cursor-pointer text-sm text-hf-muted hover:text-hf-ink transition"
                >
                    <Paperclip size={16} />
                    <span data-testid={`${testIdPrefix}-file-summary`}>
                        {files && files.length > 0
                            ? `${files.length} Datei${files.length === 1 ? "" : "en"} ausgewählt`
                            : "Dateien auswählen (Grundriss, Fotos, PDFs)"}
                    </span>
                </label>
                <input
                    id="ct-files"
                    name="files"
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={(e) => setFiles(e.target.files)}
                    className="sr-only"
                    data-testid={`${testIdPrefix}-field-files`}
                />
            </div>
            <div className="md:col-span-2">
                <label className="flex items-start gap-3 text-sm text-hf-ink">
                    <input
                        type="checkbox"
                        checked={datenschutz}
                        onChange={(e) => setDatenschutz(e.target.checked)}
                        required
                        className="mt-1 accent-[hsl(var(--hf-gold-dark))]"
                        data-testid={`${testIdPrefix}-field-datenschutz`}
                    />
                    <span>
                        Ich habe die{" "}
                        <a
                            href="/datenschutz"
                            className="underline hover:text-hf-gold-dark"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Datenschutzerklärung
                        </a>{" "}
                        gelesen und stimme der Verarbeitung meiner Daten zur
                        Bearbeitung meiner Anfrage zu.{" "}
                        <span style={{ color: "hsl(var(--hf-gold-dark))" }}>*</span>
                    </span>
                </label>
                {fieldErrors.datenschutz && (
                    <p className="mt-1 text-xs ml-7" style={{ color: "hsl(var(--hf-gold-dark))" }} data-testid={`${testIdPrefix}-err-datenschutz`}>
                        {fieldErrors.datenschutz}
                    </p>
                )}
            </div>
            {status === "error" && (
                <div
                    className="md:col-span-2 p-3 rounded text-sm"
                    style={{
                        background: "hsl(var(--hf-danger) / 0.12)",
                        color: "hsl(var(--hf-danger))",
                        border: "1px solid hsl(var(--hf-danger) / 0.3)",
                    }}
                    data-testid={`${testIdPrefix}-error`}
                >
                    {errorMsg}
                </div>
            )}
            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <HFButton
                    type="submit"
                    disabled={status === "sending"}
                    variant="primary"
                    trailing={<span aria-hidden>→</span>}
                    data-testid={`${testIdPrefix}-submit`}
                >
                    {status === "sending" ? "Wird gesendet…" : "Anfrage senden"}
                </HFButton>
                <span className="text-xs text-hf-muted">
                    Pflichtfelder mit * markiert.
                </span>
            </div>
        </form>
    );
}
